import { supabase, logAuditEvent } from './supabase';
import type { Json } from './database.types';
import { isImpersonationPreview } from './impersonationPreview';

export type IntakeFieldRow = { intake_form_id: string; field_key: string; field_value: Json };

/**
 * Upsert intake answers.
 *
 * Staff may edit any field from any view, including "view as client" (Tzvi,
 * 2026-08-03): a typo is quicker for staff to fix in the client's own view, and
 * the view is also how staff reproduce what a client is reporting. This
 * replaces the BUG-1 blanket read-only of 2026-07-23, which stopped Jane's
 * accidental preview edits but also removed the deliberate ones.
 *
 * `intake_field_data` has no per-row author column, so a staff edit is
 * otherwise indistinguishable from the client's own answer, on a form the
 * client later declares is true and complete. We record the fact in audit_log
 * instead: no schema change, invisible to the client, and it answers "who
 * changed this" after the event. Best-effort by design, a failed log must
 * never fail the save.
 *
 * count: 'exact' is preserved from the callers (R5-D/B9: RLS silently
 * filtering rows must surface as a failed save, not a false "Saved").
 */
export async function upsertIntakeFieldData(rows: IntakeFieldRow[]) {
  const result = await supabase
    .from('intake_field_data')
    .upsert(rows, { onConflict: 'intake_form_id,field_key', count: 'exact' });

  if (!result.error && isImpersonationPreview()) {
    void logAuditEvent('staff_edit_as_client', {
      tableName: 'intake_field_data',
      rowId: rows[0]?.intake_form_id,
      changes: { field_keys: rows.map((r) => r.field_key) },
    }).catch(() => {
      /* never fail a save because the audit write failed */
    });
  }

  return result;
}
